import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";

export const EMPTY_FILTERS = {
  senderIds: [],
  campaignIds: [],
  tags: [],
  autoTags: [],
  messageChannel: "ALL",
  lastMessageFrom: "ALL",
  archived: "UNARCHIVED",
};

export const AUTO_TAG_OPTIONS = [
  { id: "generic", label: "Generic lead", icon: "👤" },
  { id: "interested", label: "Interested lead", icon: "🔥" },
  { id: "not-interested", label: "Not interested lead", icon: "❄️" },
];

export const MESSAGE_CHANNEL_OPTIONS = [
  { id: "ALL", label: "All" },
  { id: "LINKEDIN", label: "LinkedIn messages" },
  { id: "SALES_NAVIGATOR", label: "Sales Navigator messages" },
  { id: "RECRUITER", label: "Recruiter messages" },
];

export const LAST_MESSAGE_FROM_OPTIONS = [
  { id: "ALL", label: "All" },
  { id: "SENDER", label: "Sender" },
  { id: "LEAD", label: "Lead" },
];

export const ARCHIVED_OPTIONS = [
  { id: "UNARCHIVED", label: "Unarchived" },
  { id: "ARCHIVED", label: "Archived" },
  { id: "ALL", label: "All" },
];

export function countActiveFilters(filters) {
  if (!filters) return 0;
  let n = 0;
  if (filters.senderIds?.length) n += 1;
  if (filters.campaignIds?.length) n += 1;
  if (filters.tags?.length) n += 1;
  if (filters.autoTags?.length) n += 1;
  if (filters.messageChannel && filters.messageChannel !== "ALL") n += 1;
  if (filters.lastMessageFrom && filters.lastMessageFrom !== "ALL") n += 1;
  if (filters.archived && filters.archived !== "UNARCHIVED") n += 1;
  return n;
}

/**
 * Query params safe on current prod API (forbidNonWhitelisted).
 * Extended filters are applied client-side via applyClientSideFilters.
 */
export function filtersToQueryParams(filters) {
  if (!filters) return {};
  const params = {};
  // Prod supports singular ids only today
  if (filters.senderIds?.length === 1) {
    params.linkedinAccountId = filters.senderIds[0];
  }
  if (filters.campaignIds?.length === 1) {
    params.campaignId = filters.campaignIds[0];
  }
  return params;
}

/**
 * Client-side filters for fields prod threads API doesn't accept yet
 * (lastMessageFrom, multi senders/campaigns, archived, etc.).
 */
export function applyClientSideFilters(threads, filters) {
  if (!filters || !Array.isArray(threads)) return threads || [];
  let rows = threads;
  if (filters.senderIds?.length > 1) {
    const set = new Set(filters.senderIds);
    rows = rows.filter((t) => set.has(t.linkedinAccount?.id));
  }
  if (filters.campaignIds?.length > 1) {
    const set = new Set(filters.campaignIds);
    rows = rows.filter((t) => set.has(t.campaign?.id));
  }
  if (filters.lastMessageFrom === "SENDER") {
    rows = rows.filter((t) => t.lastMessage?.direction === "OUTBOUND");
  } else if (filters.lastMessageFrom === "LEAD") {
    rows = rows.filter((t) => t.lastMessage?.direction === "INBOUND");
  }
  // Archive storage not on prod yet — "Archived" shows none; Unarchived/All keep all
  if (filters.archived === "ARCHIVED") {
    rows = [];
  }
  return rows;
}

function MultiSelect({ label, placeholder, options, value, onChange, searchable }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = new Set(value || []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-left hover:border-slate-300"
      >
        <span className={selected.size ? "text-slate-800 truncate" : "text-slate-400"}>
          {selected.size
            ? options
                .filter((o) => selected.has(o.id))
                .map((o) => o.label)
                .join(", ")
            : placeholder}
        </span>
        <span className="text-slate-400 ms-2">▾</span>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg max-h-56 overflow-hidden flex flex-col">
            {searchable ? (
              <div className="p-2 border-b border-slate-100">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-blue-400"
                />
              </div>
            ) : null}
            <div className="overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No options</div>
              ) : (
                filtered.map((opt) => {
                  const checked = selected.has(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        const next = new Set(selected);
                        if (checked) next.delete(opt.id);
                        else next.add(opt.id);
                        onChange([...next]);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                        checked ? "bg-blue-50 text-blue-700" : "text-slate-700"
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                          checked
                            ? "bg-blue-500 border-blue-500 text-white"
                            : "border-slate-300"
                        }`}
                      >
                        {checked ? "✓" : ""}
                      </span>
                      {opt.icon ? <span>{opt.icon}</span> : null}
                      <span className="truncate">{opt.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function SingleSelect({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value) || options[0];
  return (
    <div className="relative">
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-left hover:border-slate-300"
      >
        <span className="text-slate-800">{current?.label}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-48 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                  opt.id === value ? "text-blue-600 bg-blue-50" : "text-slate-700"
                }`}
              >
                <span>{opt.label}</span>
                {opt.id === value ? <span className="text-blue-500">✓</span> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function FiltersModal({
  open,
  draft,
  onChange,
  onClose,
  onApply,
  onClear,
  senders,
  campaigns,
  tagOptions,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const senderOptions = (senders || []).map((s) => ({
    id: s.id,
    label: s.accountName || s.name || s.id,
  }));
  const campaignOptions = (campaigns || []).map((c) => ({
    id: c.id,
    label: c.name || c.id,
  }));
  const tags = (tagOptions || []).map((t) =>
    typeof t === "string" ? { id: t, label: t } : t,
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[120] flex items-start justify-center pt-[12vh] px-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-visible">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">Filters</h2>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MultiSelect
            label="Senders"
            placeholder="Select senders"
            options={senderOptions}
            value={draft.senderIds}
            onChange={(senderIds) => onChange({ ...draft, senderIds })}
            searchable
          />
          <MultiSelect
            label="Campaigns"
            placeholder="Select campaigns"
            options={campaignOptions}
            value={draft.campaignIds}
            onChange={(campaignIds) => onChange({ ...draft, campaignIds })}
            searchable
          />
          <MultiSelect
            label="Tags"
            placeholder="Select tags"
            options={tags}
            value={draft.tags}
            onChange={(next) => onChange({ ...draft, tags: next })}
            searchable
          />
          <MultiSelect
            label="Auto tags"
            placeholder="Select auto tags"
            options={AUTO_TAG_OPTIONS}
            value={draft.autoTags}
            onChange={(autoTags) => onChange({ ...draft, autoTags })}
            searchable
          />
          <SingleSelect
            label="Message type"
            options={MESSAGE_CHANNEL_OPTIONS}
            value={draft.messageChannel}
            onChange={(messageChannel) => onChange({ ...draft, messageChannel })}
          />
          <SingleSelect
            label="Last message from"
            options={LAST_MESSAGE_FROM_OPTIONS}
            value={draft.lastMessageFrom}
            onChange={(lastMessageFrom) => onChange({ ...draft, lastMessageFrom })}
          />
          <SingleSelect
            label="Archived"
            options={ARCHIVED_OPTIONS}
            value={draft.archived}
            onChange={(archived) => onChange({ ...draft, archived })}
          />
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
          <button
            type="button"
            onClick={onClear}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-5 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600"
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
